import { useState } from "react";

import { Loader2, Pencil, Plus, RefreshCcw, Shield, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Can } from "@/features/rbac/components/Can";
import { RoleFormDialog } from "@/features/rbac/components/RoleFormDialog";
import {
  useDeleteRole,
  useEnsureSystemRoles,
  useRoles,
} from "@/features/rbac/hooks/useRoles";
import {
  GLOBAL_WILDCARD,
  PERMISSION_BY_ID,
  PERMISSION_MODULES,
} from "@/features/rbac/permissions/catalog";
import type { RbacRole } from "@/features/rbac/schemas/rbac";

/** Resumen legible de lo que concede un rol. */
function summarize(role: RbacRole): string {
  if (role.permissions.includes(GLOBAL_WILDCARD)) return "Acceso total";

  const labels = role.permissions.map((permission) => {
    if (permission.endsWith(":*")) {
      const moduleId = permission.slice(0, -2);
      const module = PERMISSION_MODULES.find((candidate) => candidate.id === moduleId);
      return module ? `${module.label} (todo)` : permission;
    }
    return PERMISSION_BY_ID[permission]?.label ?? permission;
  });

  if (labels.length <= 4) return labels.join(" · ");
  return `${labels.slice(0, 4).join(" · ")} y ${labels.length - 4} más`;
}

/**
 * Administración de roles: crear, editar y eliminar sin tocar código.
 *
 * Los roles del sistema no se pueden eliminar porque los usuarios que todavía
 * no tienen asignación explícita resuelven sus permisos a través de ellos.
 */
export function RolesEditor() {
  const { data: roles = [], isLoading } = useRoles();
  const deleteRole = useDeleteRole();
  const ensureSystemRoles = useEnsureSystemRoles();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<RbacRole | null>(null);

  const openCreate = () => {
    setEditingRole(null);
    setDialogOpen(true);
  };

  const openEdit = (role: RbacRole) => {
    setEditingRole(role);
    setDialogOpen(true);
  };

  const handleDelete = async (role: RbacRole) => {
    try {
      await deleteRole.mutateAsync(role.id);
      toast.success(`Rol "${role.name}" eliminado`);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "No se pudo eliminar el rol"
      );
    }
  };

  const handleRestore = async () => {
    try {
      const created = await ensureSystemRoles.mutateAsync();
      toast.success(
        created.length === 0
          ? "No faltaba ningún rol del sistema"
          : `Roles restaurados: ${created.join(", ")}`
      );
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "No se pudieron restaurar los roles"
      );
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold">Roles y permisos</h2>
          <p className="text-sm text-muted-foreground">
            Define qué puede hacer cada rol. Los cambios se aplican de inmediato, sin
            desplegar. Los roles del sistema también rigen para los usuarios que aún
            conservan permisos heredados.
          </p>
        </div>

        <div className="flex shrink-0 gap-2">
          <Can permission="roles:edit">
            <Button
              variant="outline"
              size="sm"
              onClick={() => void handleRestore()}
              disabled={ensureSystemRoles.isPending}
              title="Vuelve a crear los roles del sistema que falten, sin tocar los existentes"
            >
              {ensureSystemRoles.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCcw className="mr-2 h-4 w-4" />
              )}
              Restaurar roles del sistema
            </Button>
          </Can>

          <Can permission="roles:create">
            <Button size="sm" onClick={openCreate}>
              <Plus className="mr-2 h-4 w-4" />
              Nuevo rol
            </Button>
          </Can>
        </div>
      </div>

      {roles.length === 0 && (
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            Todavía no hay roles definidos. Usa «Restaurar roles del sistema» para crear
            los diez roles equivalentes a los permisos anteriores.
          </CardContent>
        </Card>
      )}

      <div className="grid gap-3 md:grid-cols-2">
        {roles.map((role) => (
          <Card key={role.id}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <span className="truncate">{role.name}</span>
                    {role.system && (
                      <Badge variant="secondary" className="shrink-0 gap-1">
                        <Shield className="h-3 w-3" />
                        Sistema
                      </Badge>
                    )}
                  </CardTitle>
                  <CardDescription className="line-clamp-2">
                    {role.description}
                  </CardDescription>
                </div>

                <div className="flex shrink-0 gap-1">
                  <Can permission="roles:edit">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEdit(role)}
                      title="Editar rol"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </Can>

                  <Can permission="roles:delete">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => void handleDelete(role)}
                      disabled={role.system || deleteRole.isPending}
                      title={
                        role.system
                          ? "Los roles del sistema no se pueden eliminar"
                          : "Eliminar rol"
                      }
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </Can>
                </div>
              </div>
            </CardHeader>

            <CardContent className="pt-0">
              <p className="text-sm text-muted-foreground">{summarize(role)}</p>
              <code className="mt-2 block text-xs text-muted-foreground">{role.id}</code>
            </CardContent>
          </Card>
        ))}
      </div>

      <RoleFormDialog
        role={editingRole}
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      />
    </div>
  );
}
